import { Link, useNavigate } from "@tanstack/react-router";
import { Check, Loader2, Plus } from "lucide-react";
import { Field, InfoLabel } from "@/components/catalyst/Fieldset";
import { InputGroup } from "@/components/catalyst/Input";
import { Select } from "@/components/catalyst/Select";
import { useHighlight } from "@/hooks/use-highlight";
import type { BillingEntitySection as BillingEntitySectionType } from "../types";

interface BillingEntity {
	id: string;
	name: string;
}

interface BillingEntitySectionProps {
	appId: string;
	billingEntities: BillingEntity[];
	billingEntity: BillingEntitySectionType;
}

export function BillingEntitySection({
	appId,
	billingEntities,
	billingEntity,
}: BillingEntitySectionProps) {
	const navigate = useNavigate();
	const { ref: highlightRef, isHighlighted } = useHighlight("paymentMethod");

	const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value;
		if (value === "__add_new__") {
			navigate({
				to: "/finances",
				search: {
					add: true,
					tab: "payment_methods",
					successRedirect: `/apps/${appId}`,
				},
			});
		} else if (value) {
			billingEntity.update(value);
		}
	};

	return (
		<div
			ref={highlightRef}
			className={`${isHighlighted ? "animate-highlight" : ""}`}
		>
			<Field>
				<InfoLabel info="Select the payment method used to fund payouts for this app's referrals.">
					Payment Method
				</InfoLabel>

				{billingEntities.length === 0 ? (
					<InputGroup>
						<Link
							to="/finances"
							search={{
								add: true,
								tab: "payment_methods",
								successRedirect: `/apps/${appId}`,
							}}
							className="group flex items-center gap-4 rounded-lg border-2 border-dashed border-base-content/20 bg-base-200/30 px-4 py-3 transition-all hover:border-primary/50 hover:bg-primary/5"
						>
							<div className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors group-hover:bg-primary/20">
								<Plus className="size-5" />
							</div>
							<div className="flex flex-col">
								<span className="font-medium text-base-content">
									Add Payment Method
								</span>
								<span className="text-sm text-base-content/60">
									Required to send payouts for this app
								</span>
							</div>
						</Link>
					</InputGroup>
				) : (
					<InputGroup>
						<div className="flex items-center gap-3">
							<div className="relative flex-1">
								<Select
									value={billingEntity.value ?? ""}
									onChange={handleChange}
									aria-label="Payment Method"
								>
									{!billingEntity.value && (
										<option value="" disabled>
											Select a payment method...
										</option>
									)}
									{billingEntities.map((entity) => (
										<option key={entity.id} value={entity.id}>
											{entity.name}
										</option>
									))}
									<option value="__add_new__">+ Add New Payment Method</option>
								</Select>

								{billingEntity.isSaving && (
									<Loader2 className="absolute right-8 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-base-content/50" />
								)}
								{billingEntity.isSaved && (
									<Check className="absolute right-8 top-1/2 h-4 w-4 -translate-y-1/2 text-base-content/50" />
								)}
							</div>
						</div>
					</InputGroup>
				)}
			</Field>
		</div>
	);
}
